/**
 * RelationshipPill — canonical relationship type color config and reusable pill component.
 *
 * Single source of truth for all guardian relationship badge/pill styling throughout the app.
 * Covers: Parent, Grandparent, Sibling, Aunt, Uncle, Other, etc.
 *
 * Usage (component):
 *   <RelationshipPill relationship="Parent" />
 *   <RelationshipPill relationship="SIBLING" className="opacity-80" />
 *
 * Usage (utilities):
 *   getRelationshipConfig('parent')          → full config object
 *   getRelationshipPillClassName('aunt')     → "bg-purple-100 border-purple-200 text-purple-700 font-semibold text-xs rounded border"
 *
 * Color scheme:
 *   Parent        → emerald-100 (primary caregiver)
 *   Grandparent   → blue-100 (extended family)
 *   Sibling       → cyan-100 (sibling)
 *   Aunt/Uncle    → purple-100 (extended family)
 *   Cousin        → pink-100 (less frequent)
 *   Other         → gray-100 (catch-all)
 */

import React from 'react';

// ─── Config type ──────────────────────────────────────────────────────────────

export interface RelationshipConfig {
  /** Display label (e.g. "Parent", "Grandparent") */
  label: string;
  /** Light pill background (e.g. bg-emerald-100) */
  bg: string;
  /** Pill text color (e.g. text-emerald-700) */
  text: string;
  /** Pill border color (e.g. border-emerald-200) */
  border: string;
}

// ─── Canonical map ────────────────────────────────────────────────────────────

const BASE = 'font-semibold text-xs rounded border';

const RELATIONSHIP_MAP: Record<string, RelationshipConfig> = {
  // ── Parent ──────────────────────────────────────────────────────────────────
  PARENT: {
    label: 'Parent',
    bg: 'bg-emerald-100',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
  },

  // ── Grandparent ─────────────────────────────────────────────────────────────
  GRANDPARENT: {
    label: 'Grandparent',
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    border: 'border-blue-200',
  },

  // ── Sibling ─────────────────────────────────────────────────────────────────
  SIBLING: {
    label: 'Sibling',
    bg: 'bg-cyan-100',
    text: 'text-cyan-700',
    border: 'border-cyan-200',
  },

  // ── Aunt ────────────────────────────────────────────────────────────────────
  AUNT: {
    label: 'Aunt',
    bg: 'bg-purple-100',
    text: 'text-purple-700',
    border: 'border-purple-200',
  },

  // ── Uncle ───────────────────────────────────────────────────────────────────
  UNCLE: {
    label: 'Uncle',
    bg: 'bg-purple-100',
    text: 'text-purple-700',
    border: 'border-purple-200',
  },

  // ── Cousin ──────────────────────────────────────────────────────────────────
  COUSIN: {
    label: 'Cousin',
    bg: 'bg-pink-100',
    text: 'text-pink-700',
    border: 'border-pink-200',
  },

  // ── Other (default fallback) ────────────────────────────────────────────────
  OTHER: {
    label: 'Other',
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    border: 'border-gray-200',
  },
};

const DEFAULT_CONFIG: RelationshipConfig = {
  label: '',
  bg: 'bg-gray-100',
  text: 'text-gray-700',
  border: 'border-gray-200',
};

// ─── Utility functions ────────────────────────────────────────────────────────

/**
 * Look up the canonical config for a relationship string.
 * Normalises to uppercase; falls back to 'Other' for unknown relationships.
 */
export function getRelationshipConfig(relationship: string): RelationshipConfig {
  if (!relationship) return DEFAULT_CONFIG;
  const key = relationship.trim().toUpperCase();
  return RELATIONSHIP_MAP[key] ?? RELATIONSHIP_MAP.OTHER;
}

/**
 * Returns the full Tailwind class string for a relationship pill.
 *
 * @example
 *   className={`px-2 py-0.5 ${getRelationshipPillClassName('Parent')}`}
 */
export function getRelationshipPillClassName(relationship: string): string {
  const config = getRelationshipConfig(relationship);
  return `${config.bg} ${config.text} ${config.border} ${BASE}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface RelationshipPillProps {
  /** Relationship type: Parent | Grandparent | Sibling | Aunt | Uncle | Cousin | Other */
  relationship: string;
  /** Override display label (defaults to canonical label from config) */
  label?: string;
  /** Extra Tailwind classes appended to the root element */
  className?: string;
}

/**
 * RelationshipPill — canonical relationship type badge component.
 *
 * Renders as: font-semibold · text-xs · rounded · border + relationship-specific
 * light background, colored text, and matching border.
 *
 * Standardized layout: inline-flex items-center · px-2.5 py-0.5
 */
export function RelationshipPill({
  relationship,
  label,
  className = '',
}: RelationshipPillProps) {
  const config = getRelationshipConfig(relationship);
  const displayLabel = label ?? config.label;

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 ${getRelationshipPillClassName(relationship)} ${className}`.trim()}
    >
      {displayLabel}
    </span>
  );
}

export default RelationshipPill;
